import React from 'react';
import styled from '@emotion/styled';
import { Grid, Typography, Fade } from '@mui/material';
import { TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { selectUserEmail } from '../../../redux/selectors/authSelectors';
import UserDot from './UserDot';
import { getInitials , convertStylesToStyled } from '../../../_utils/utils';
import TimelineTimestamp from '../TimelineTimestamp';
import DateTimeDisplay from '../DateTimeDisplay';
import { Note as NoteType } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import { useTheme } from '@emotion/react';
import PolarisIcon from '../PolarisIcon';

const Note = React.forwardRef(({ note }: { note: NoteType }, forwardRef) => {
  if (!note) return null;

  const { created_by, created_at, message } = note;
  const myEmail = useAppSelector(selectUserEmail);
  const isMe = created_by && created_by.email ? created_by.email?.toLowerCase() === myEmail : false;
  const usersName = created_by ? (created_by?.first_name + ' ' + created_by?.last_name) : 'System Message';
  const theme = useTheme();
  const color = created_by && created_by.color ? created_by.color : theme.palette.primary.main;

  return (
    <Fade in timeout={600}>
      <StyledTimelineItem ref={forwardRef}>
        <TimelineTimestamp datetime={created_at} />
        <TimelineSeparator>
          {isMe ? <UserDot /> : <StyledTimelineDot bg={color}>{created_by ? getInitials(created_by) : <PolarisIcon icon="robot_outline" height={20}/>}</StyledTimelineDot>}
          <TimelineConnector />
        </TimelineSeparator>
        <StyledTimelineContent>
          <Grid container justifyContent="space-between">
            <Typography variant="subtitle1" style={{ marginBottom: 1, color: color }}>
              <strong>
                {usersName}
                {isMe ? ' (you)' : ''}:
              </strong>
            </Typography>
          </Grid>
          <div style={{ whiteSpace: 'pre-line' }}>{message}</div>

          {/* only display on mobile */}
          <StyledDateTimeDisplay singleLine>{created_at}</StyledDateTimeDisplay>
        </StyledTimelineContent>
      </StyledTimelineItem>
    </Fade>
  );
});

export default React.memo(Note);

const StyledTimelineItem = styled(TimelineItem)(
  ({ theme }) => `
  min-height: 130px;

  & .MuiTypography-subtitle1 {
    color: ${theme.palette.text.primary};
  }

  ${convertStylesToStyled(theme.customOverrides?.TaskNote)}
`
);

const StyledDateTimeDisplay = styled(DateTimeDisplay)(
  ({ theme }) => `
  padding-top: ${theme.spacing(1.5)};
  font-size: 85%;
  opacity: 0.5;

  ${theme.breakpoints.up('sm')} {
    display: none;
  }
`
);

const StyledTimelineDot = styled(TimelineDot)<{ bg: string }>`
  height: 35px;
  width: 35px;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  line-height: 1;
  color: ${({ theme, bg }) => theme.palette.getContrastText(bg)};
  background-color: ${({ bg, theme }) => bg};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const StyledTimelineContent = styled(TimelineContent)(
  ({ theme }) => `
  font-weight: ${theme.typography.fontWeightNormal};
  background: ${theme.palette.action.hover};
  margin-left: ${theme.spacing(3)};
  padding: ${theme.spacing(2, 3, 2.5)};
  margin-top: 5px;
  margin-bottom: ${theme.spacing(1.5)};
  border-radius: ${theme.shape.borderRadius}px;
  position: relative;
  &:before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border: 10px solid;
    border-color: transparent ${theme.palette.action.hover} transparent transparent;
    left: -20px;
    top: 15px;
  }
`
);
